import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { TransportType } from 'types/QuoteRequest';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

type GetDATLaneHistoryParams = {
  transportType: string;
  originCity: string;
  originState: string;
  destinationCity: string;
  destinationState: string;
  lookbackMonths?: number;
};

type PriceRange = {
  rateUsd: number;
  highUsd: number;
  lowUsd: number;
};

export type DATLaneHistoryEntry = {
  year: number;
  month: number;
  perMile: PriceRange;
  averageFuelSurchargePerMileUsd: number;
  perTrip: PriceRange;
  averageFuelSurchargePerTripUsd: number;
};

export type DATLaneHistoryResponse = {
  inputtedTransportType: TransportType;
  submittedTransportType: TransportType;
  rates: DATLaneHistoryEntry[];
};

export default async function getDATLaneHistory(
  request: GetDATLaneHistoryParams
): Promise<Result<DATLaneHistoryResponse, ApiError>> {
  try {
    const queryData = buildDATLaneHistoryQuery(request);

    const response = await axios.get<DATLaneHistoryResponse>(
      `quote/private/lane-history/dat?${queryData}`
    );

    return ok(response.data);
  } catch (error) {
    captureException(error, { functionName: 'getDATLaneHistory' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to get DAT lane history' });
    }

    return err({
      message: error.response?.data || 'Failed to get DAT lane history',
    });
  }
}

const buildDATLaneHistoryQuery = ({
  originCity,
  originState,
  destinationCity,
  destinationState,
  transportType,
  lookbackMonths = 5,
}: GetDATLaneHistoryParams): string => {
  // Ensure valid lookback
  const safeLookback =
    typeof lookbackMonths === 'number' && Number.isFinite(lookbackMonths)
      ? Math.max(2, lookbackMonths)
      : 5;

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1–12

  // To = previous month (DAT expects a closed window ending last month)
  let toMonth = currentMonth - 1;
  let toYear = currentYear;
  if (toMonth < 1) {
    toYear--;
    toMonth = 12 + toMonth;
  }

  // From = safeLookback months before current; roll year back when needed
  let fromMonth = currentMonth - safeLookback;
  let fromYear = currentYear;
  if (fromMonth < 1) {
    const yearsBack = Math.ceil(-fromMonth / 12) || 1;
    fromYear -= yearsBack;
    fromMonth = fromMonth + 12 * yearsBack;
  }

  // When lookback was an exact multiple of 12, fromMonth becomes 0. Normalize to Dec of prior year
  if (fromMonth < 1) {
    fromMonth += 12;
    fromYear -= 1;
  }

  const params = {
    originCity,
    originState,
    destinationCity,
    destinationState,
    rateType: 'SPOT',
    equipment: transportType,
    fromYear: fromYear.toString(),
    fromMonth: fromMonth.toString(),
    toYear: toYear.toString(),
    toMonth: toMonth.toString(),
  };

  return new URLSearchParams(params).toString();
};
